import MuiCard from '@material-ui/core/Card';
import MuiCardContent from '@material-ui/core/CardContent';
import WbIncandescentOutlinedIcon from '@material-ui/icons/WbIncandescentOutlined';
import { bodySmall } from '@monorepo/shared/styles/text';
import * as importedTheme from '@monorepo/shared/themes/default';
import React from 'react';
import styled from 'styled-components';

export enum InfoCardVariant {
  INFO = 'info',
  WARNING = 'warning',
}

const StyledWbIncandescentOutlinedIcon = styled(WbIncandescentOutlinedIcon)`
  transform: rotate(180deg);
`;

const getColors = (variant: InfoCardVariant, theme: typeof importedTheme) => {
  switch (variant) {
    case InfoCardVariant.INFO:
      return {
        backgroundColor: theme.colors.green08,
        textColor: theme.colors.gray666,
        iconColor: theme.colors.green,
      };
    case InfoCardVariant.WARNING:
      return {
        backgroundColor: theme.colors.warningFill,
        textColor: theme.colors.warningContent,
        iconColor: theme.colors.orange,
      };
    default: {
      const exhaustiveCheck: never = variant;
      throw new Error(`Unhandled variant: ${exhaustiveCheck}`);
    }
  }
};

const Card = styled(MuiCard)<{ $variant: InfoCardVariant }>`
  background-color: ${({ theme, $variant }) =>
    getColors($variant, theme).backgroundColor};
  box-shadow: none;
`;

const CardContent = styled(MuiCardContent)<{ $variant: InfoCardVariant }>`
  ${bodySmall}
  display: flex;
  align-items: center;
  padding: 0.5rem;
  color: ${({ theme, $variant }) => getColors($variant, theme).textColor};

  &:last-child {
    padding: 0.5rem;
  }
`;

const LightbulbContainer = styled.div<{ $variant: InfoCardVariant }>`
  padding: 0 0.5rem 0 0;

  & svg {
    width: 1.375rem;
    fill: ${({ theme, $variant }) => getColors($variant, theme).iconColor};
  }
`;

type InfoCardProps = Omit<React.ComponentProps<typeof MuiCard>, 'variant'> & {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  variant?: InfoCardVariant;
};

export function InfoCard({
  children,
  icon,
  variant = InfoCardVariant.INFO,
  ...props
}: InfoCardProps) {
  return (
    <Card $variant={variant} {...props}>
      <CardContent $variant={variant}>
        <LightbulbContainer $variant={variant}>
          {icon || <StyledWbIncandescentOutlinedIcon />}
        </LightbulbContainer>
        {children}
      </CardContent>
    </Card>
  );
}
